import { create } from 'zustand';
import {
  createEmptyDocument,
  type DesignDocument,
  type DesignObject,
  type ImageMask,
  type ProductModel,
  type TextObject,
  type ViewId,
} from '@ox/schema';
import { measureTextBoxMm, fitTextToPrintArea, bakeCurveLayoutMm } from '../lib/textMeasure';
import { catalogColorKeyForHex, normalizeColorHex } from '../lib/modelAxes';

type Command = {
  undo: () => void;
  redo: () => void;
};

interface EditorState {
  model: ProductModel | null;
  document: DesignDocument;
  activeView: ViewId;
  selectedId: string | null;
  tool: 'select' | 'text' | 'upload' | 'products';
  showGuides: boolean;
  previewMode: boolean;
  panel: 'none' | 'text' | 'addText' | 'image' | 'design' | 'products' | 'sizes' | 'gallery';
  past: Command[];
  future: Command[];
  lowResWarning: boolean;

  setModel: (model: ProductModel, colorKey?: string) => void;
  /** Switch garment while keeping design objects (scaled to the new print areas). */
  changeModel: (model: ProductModel, colorKey?: string) => void;
  setColor: (colorKey: string) => void;
  setGarmentPrintType: (type: 'no_white_base' | 'white_base' | undefined) => void;
  setFranelaReactiva: (v: boolean) => void;
  setActiveView: (view: ViewId) => void;
  setTool: (tool: EditorState['tool']) => void;
  setPanel: (panel: EditorState['panel']) => void;
  setPreviewMode: (v: boolean) => void;
  select: (id: string | null) => void;
  addText: (opts?: { content?: string; fontFamily?: string; color?: string }) => void;
  addImage: (
    assetId: string,
    widthMm: number,
    heightMm: number,
    opts?: {
      mask?: ImageMask;
      maskScale?: number;
      maskOffsetX?: number;
      maskOffsetY?: number;
      effects?: { grayscale?: boolean; filter?: import('@ox/schema').ImageFilter };
    },
  ) => void;
  /** Place catalog artwork by reference (no Asset upload). */
  addGalleryImage: (
    galleryCode: string,
    widthMm: number,
    heightMm: number,
  ) => void;
  updateObject: (id: string, patch: Partial<DesignObject>) => void;
  deleteSelected: () => void;
  centerH: () => void;
  centerV: () => void;
  bringForward: () => void;
  sendBackward: () => void;
  moveSelectedToView: (viewId: ViewId) => void;
  undo: () => void;
  redo: () => void;
  replaceDocument: (doc: DesignDocument) => void;
  /** Re-measure all text boxes (e.g. after fonts load or design hydrate). */
  remasureAllText: () => void;
  setLowResWarning: (v: boolean) => void;
}

function uid() {
  return `o_${Math.random().toString(36).slice(2, 10)}`;
}

function activeObjects(state: EditorState): DesignObject[] {
  return state.document.views.find((v) => v.viewId === state.activeView)?.objects ?? [];
}

function withObjects(
  doc: DesignDocument,
  viewId: ViewId,
  objects: DesignObject[],
): DesignDocument {
  return {
    ...doc,
    updatedAt: new Date().toISOString(),
    views: doc.views.map((v) => (v.viewId === viewId ? { ...v, objects } : v)),
  };
}

function printArea(model: ProductModel | null, view: ViewId) {
  return model?.printAreas[view] ?? { x: 0, y: 0, width: 300, height: 400, unit: 'mm' as const };
}

function parseHexColor(raw?: string): string | undefined {
  if (!raw) return undefined;
  const t = raw.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/i.test(t)) return undefined;
  return `#${t.toLowerCase()}`;
}

function isDarkHex(hex: string): boolean {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

/** Rewrite mockup URLs so `color=` matches the chosen hex (custom garment color). */
function mockupsForHex(
  base: ProductModel['colors'][number]['mockups'],
  hex: string,
): ProductModel['colors'][number]['mockups'] {
  const bare = hex.replace(/^#/, '').toLowerCase();
  const out: ProductModel['colors'][number]['mockups'] = {};
  for (const [view, asset] of Object.entries(base)) {
    if (!asset) continue;
    try {
      const u = new URL(asset.url, 'https://ox.local');
      u.searchParams.set('color', bare);
      const next =
        /^https?:\/\//i.test(asset.url) ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
      out[view as ViewId] = { ...asset, url: next };
    } catch {
      out[view as ViewId] = {
        ...asset,
        url: asset.url.replace(/([?&]color=)[^&]*/i, `$1${bare}`),
      };
    }
  }
  return out;
}

/**
 * Free hex from PDP color picker (`ox_color=a1b2c3`): inject a synthetic catalog color
 * so the canvas can tint mockups.
 */
function withCustomHexColor(model: ProductModel, preferred?: string): {
  model: ProductModel;
  colorKey: string;
  colorHex?: string;
} {
  const trimmed = preferred?.trim() ?? '';
  if (trimmed && model.colors.some((c) => c.key === trimmed)) {
    const hex = resolveColorHexFromCatalog(model, trimmed);
    return { model, colorKey: trimmed, colorHex: hex };
  }

  const hex = parseHexColor(trimmed);
  if (hex) {
    const key = hex.replace(/^#/, '').toLowerCase();
    if (model.colors.some((c) => c.key === key)) {
      return { model, colorKey: key, colorHex: hex };
    }
    // Same hex as a catalog swatch (`negro` = #0c0c0c): keep that SKU key.
    const catalogKey = catalogColorKeyForHex(model, key);
    if (catalogKey && normalizeColorHex(model.colors.find((c) => c.key === catalogKey)?.hex) === key) {
      const catalogHex = parseHexColor(model.colors.find((c) => c.key === catalogKey)?.hex) ?? hex;
      return { model, colorKey: catalogKey, colorHex: catalogHex };
    }
    const base = model.colors[0];
    if (!base) {
      return { model, colorKey: key, colorHex: hex };
    }
    const injected = {
      key,
      label: 'Personalizado',
      hex,
      isDark: isDarkHex(hex),
      available: true,
      mockups: mockupsForHex(base.mockups, hex),
    };
    return {
      model: { ...model, colors: [...model.colors, injected] },
      colorKey: key,
      colorHex: hex,
    };
  }

  const fallback = model.colors[0]?.key || 'negro';
  return {
    model,
    colorKey: fallback,
    colorHex: resolveColorHexFromCatalog(model, fallback),
  };
}

function resolveColorHexFromCatalog(model: ProductModel, colorKey: string): string | undefined {
  const raw = model.colors.find((c) => c.key === colorKey)?.hex?.trim();
  if (!raw) return parseHexColor(colorKey);
  return parseHexColor(raw);
}

/** @deprecated path kept via withCustomHexColor */
function resolveColorKey(model: ProductModel, preferred?: string) {
  return withCustomHexColor(model, preferred).colorKey;
}

/** Normalize catalog hex for persistence on the design document. */
function resolveColorHex(model: ProductModel, colorKey: string): string | undefined {
  return resolveColorHexFromCatalog(model, colorKey);
}

function scaleObject(obj: DesignObject, sx: number, sy: number): DesignObject {
  const s = Math.min(sx, sy);
  if (obj.type === 'text') {
    return {
      ...obj,
      x: obj.x * sx,
      y: obj.y * sy,
      width: obj.width * s,
      height: obj.height * s,
      fontSize: Math.max(2, obj.fontSize * s),
    };
  }
  return {
    ...obj,
    x: obj.x * sx,
    y: obj.y * sy,
    width: obj.width * s,
    height: obj.height * s,
  };
}

function remapDocumentToModel(
  doc: DesignDocument,
  from: ProductModel | null,
  to: ProductModel,
  colorKey: string,
): DesignDocument {
  const viewIds = new Set(to.views.map((v) => v.id));
  const views = doc.views
    .filter((v) => viewIds.has(v.viewId))
    .map((v) => {
      const oldArea = printArea(from, v.viewId);
      const newArea = printArea(to, v.viewId);
      const sx = newArea.width / Math.max(oldArea.width, 0.01);
      const sy = newArea.height / Math.max(oldArea.height, 0.01);
      const same =
        !from ||
        (Math.abs(sx - 1) < 0.001 && Math.abs(sy - 1) < 0.001);
      return {
        ...v,
        printAreaId: `${v.viewId}-main`,
        objects: same ? v.objects : v.objects.map((o) => scaleObject(o, sx, sy)),
      };
    });

  // Ensure every model view exists in the document
  for (const mv of to.views) {
    if (!views.some((v) => v.viewId === mv.id)) {
      views.push({
        viewId: mv.id,
        printAreaId: `${mv.id}-main`,
        objects: [],
      });
    }
  }

  return {
    ...doc,
    templateId: to.templateId,
    selection: {
      ...doc.selection,
      colorKey,
      colorHex: resolveColorHex(to, colorKey),
    },
    views,
    updatedAt: new Date().toISOString(),
  };
}

function remasureTextObject(obj: TextObject, area: { width: number; height: number }): TextObject {
  const curve = obj.curve ?? 'none';
  const cx = obj.x + obj.width / 2;
  const cy = obj.y + obj.height / 2;

  if (curve === 'arc-up' || curve === 'arc-down') {
    const baked = bakeCurveLayoutMm({
      content: obj.content,
      fontFamily: obj.fontFamily,
      fontSize: obj.fontSize,
      fontWeight: obj.fontWeight,
      curve,
      curveRadius: obj.curveRadius ?? 80,
    });
    let x = cx - baked.width / 2;
    let y = cy - baked.height / 2;
    x = Math.min(Math.max(x, -baked.width * 0.15), area.width - baked.width * 0.85);
    y = Math.min(Math.max(y, -baked.height * 0.15), area.height - baked.height * 0.85);
    return {
      ...obj,
      width: baked.width,
      height: baked.height,
      x,
      y,
      curveRadius: baked.curveRadius,
      curveGlyphs: baked.curveGlyphs,
    };
  }

  const box = measureTextBoxMm({
    content: obj.content,
    fontFamily: obj.fontFamily,
    fontSize: obj.fontSize,
    fontWeight: obj.fontWeight,
    lineHeight: obj.lineHeight,
    curve: 'none',
  });
  let x = cx - box.width / 2;
  let y = cy - box.height / 2;
  x = Math.min(Math.max(x, -box.width * 0.15), area.width - box.width * 0.85);
  y = Math.min(Math.max(y, -box.height * 0.15), area.height - box.height * 0.85);
  return {
    ...obj,
    width: box.width,
    height: box.height,
    x,
    y,
    curve: 'none',
    curveGlyphs: undefined,
  };
}

export const useEditorStore = create<EditorState>((set, get) => ({
  model: null,
  document: createEmptyDocument('polo-clasico-unisex', 'negro'),
  activeView: 'front',
  selectedId: null,
  tool: 'select',
  showGuides: true,
  previewMode: false,
  panel: 'none',
  past: [],
  future: [],
  lowResWarning: false,

  setModel(model, colorKey) {
    // Treat "" from empty HTML color="" the same as omitted (?? alone keeps "").
    const resolved = withCustomHexColor(model, colorKey);
    const viewIds = resolved.model.views.map((v) => v.id);
    set({
      model: resolved.model,
      document: createEmptyDocument(
        resolved.model.templateId,
        resolved.colorKey,
        'chest',
        resolved.colorHex,
        viewIds,
      ),
      activeView: viewIds[0] ?? 'front',
      selectedId: null,
      past: [],
      future: [],
    });
  },

  changeModel(model, colorKey) {
    const state = get();
    const resolved = withCustomHexColor(
      model,
      colorKey ?? state.document.selection.colorKey,
    );
    const nextActive =
      resolved.model.views.some((v) => v.id === state.activeView)
        ? state.activeView
        : (resolved.model.views[0]?.id ?? 'front');
    set({
      model: resolved.model,
      document: remapDocumentToModel(state.document, state.model, resolved.model, resolved.colorKey),
      activeView: nextActive,
      selectedId: null,
      panel: 'none',
      past: [],
      future: [],
    });
  },

  setColor(colorKey) {
    const { document, model } = get();
    if (!model) {
      set({
        document: {
          ...document,
          selection: { ...document.selection, colorKey },
          updatedAt: new Date().toISOString(),
        },
      });
      return;
    }
    const resolved = withCustomHexColor(model, colorKey);
    set({
      model: resolved.model,
      document: {
        ...document,
        selection: {
          ...document.selection,
          colorKey: resolved.colorKey,
          colorHex: resolved.colorHex,
        },
        updatedAt: new Date().toISOString(),
      },
    });
  },

  setGarmentPrintType(type) {
    const { document } = get();
    set({
      document: {
        ...document,
        selection: {
          ...document.selection,
          garmentPrintType: type,
        },
        updatedAt: new Date().toISOString(),
      },
    });
  },

  setFranelaReactiva(v) {
    const { document } = get();
    set({
      document: {
        ...document,
        selection: {
          ...document.selection,
          franelaReactiva: v,
        },
        updatedAt: new Date().toISOString(),
      },
    });
  },

  setActiveView(view) {
    set({ activeView: view, selectedId: null, panel: 'none' });
  },

  setTool(tool) {
    set({ tool, panel: tool === 'products' ? 'products' : get().panel });
  },

  setPanel(panel) {
    set({ panel });
  },

  setPreviewMode(v) {
    set({ previewMode: v, showGuides: !v });
  },

  select(id) {
    const obj = activeObjects(get()).find((o) => o.id === id);
    let panel: EditorState['panel'] = 'none';
    if (obj?.type === 'text') panel = 'text';
    else if (obj?.type === 'image') panel = 'design';
    else if (obj) panel = 'design';
    set({ selectedId: id, panel, tool: 'select' });
  },

  addText(opts) {
    const state = get();
    const area = printArea(state.model, state.activeView);
    const content = (opts?.content ?? 'Texto').slice(0, 200) || 'Texto';
    const fontFamily = opts?.fontFamily ?? 'Arial, sans-serif';
    const fontWeight = 700;
    const lineHeight = 1.2;
    const fitted = fitTextToPrintArea(
      { content, fontFamily, fontWeight, lineHeight },
      area,
    );
    const obj: TextObject = {
      id: uid(),
      type: 'text',
      content,
      fontFamily,
      fontSize: fitted.fontSize,
      fontWeight,
      color: opts?.color ?? '#111111',
      align: 'center',
      lineHeight,
      curve: 'none',
      curveRadius: 80,
      x: fitted.x,
      y: fitted.y,
      width: fitted.width,
      height: fitted.height,
      unit: 'mm',
      rotation: 0,
      opacity: 1,
      locked: false,
    };
    const prev = activeObjects(state);
    const next = [...prev, obj];
    const cmd: Command = {
      undo: () =>
        set({
          document: withObjects(get().document, state.activeView, prev),
          selectedId: null,
        }),
      redo: () =>
        set({
          document: withObjects(get().document, state.activeView, next),
          selectedId: obj.id,
          panel: 'text',
        }),
    };
    set({
      document: withObjects(state.document, state.activeView, next),
      selectedId: obj.id,
      panel: 'text',
      past: [...state.past, cmd],
      future: [],
    });
  },

  addImage(assetId, widthMm, heightMm, opts) {
    const state = get();
    const area = printArea(state.model, state.activeView);
    // Sizes come pre-fitted; only clamp if they would overflow the print area.
    const scale = Math.min(
      1,
      area.width / Math.max(widthMm, 1e-6),
      area.height / Math.max(heightMm, 1e-6),
    );
    const w = widthMm * scale;
    const h = heightMm * scale;
    const obj: DesignObject = {
      id: uid(),
      type: 'image',
      assetId,
      mask: opts?.mask ?? 'none',
      maskScale: opts?.maskScale ?? 1,
      maskOffsetX: opts?.maskOffsetX ?? 0,
      maskOffsetY: opts?.maskOffsetY ?? 0,
      flipX: false,
      flipY: false,
      x: (area.width - w) / 2,
      y: (area.height - h) / 2,
      width: w,
      height: h,
      unit: 'mm',
      rotation: 0,
      opacity: 1,
      locked: false,
      ...(opts?.effects ? { effects: opts.effects } : {}),
    };
    const prev = activeObjects(state);
    const next = [...prev, obj];
    set({
      document: withObjects(state.document, state.activeView, next),
      selectedId: obj.id,
      panel: 'design',
      past: [
        ...state.past,
        {
          undo: () => set({ document: withObjects(get().document, state.activeView, prev) }),
          redo: () => set({ document: withObjects(get().document, state.activeView, next) }),
        },
      ],
      future: [],
    });
  },

  addGalleryImage(galleryCode, widthMm, heightMm) {
    const state = get();
    const area = printArea(state.model, state.activeView);
    // Sizes are already contain-fitted to the print area — only clamp if oversized.
    const scale = Math.min(1, area.width / Math.max(widthMm, 1e-6), area.height / Math.max(heightMm, 1e-6));
    const w = widthMm * scale;
    const h = heightMm * scale;
    const obj: DesignObject = {
      id: uid(),
      type: 'image',
      galleryCode,
      mask: 'none',
      maskScale: 1,
      maskOffsetX: 0,
      maskOffsetY: 0,
      flipX: false,
      flipY: false,
      x: (area.width - w) / 2,
      y: (area.height - h) / 2,
      width: w,
      height: h,
      unit: 'mm',
      rotation: 0,
      opacity: 1,
      locked: false,
    };
    const prev = activeObjects(state);
    const next = [...prev, obj];
    set({
      document: withObjects(state.document, state.activeView, next),
      selectedId: obj.id,
      panel: 'design',
      past: [
        ...state.past,
        {
          undo: () => set({ document: withObjects(get().document, state.activeView, prev) }),
          redo: () => set({ document: withObjects(get().document, state.activeView, next) }),
        },
      ],
      future: [],
    });
  },

  updateObject(id, patch) {
    const state = get();
    const prev = activeObjects(state);
    const next = prev.map((o) => {
      if (o.id !== id) return o;
      const merged = { ...o, ...patch } as DesignObject;
      if (merged.type !== 'text') return merged;

      const curve = merged.curve ?? 'none';
      // Transformer scale passes width+fontSize together — keep those dimensions.
      const isUniformScale =
        'width' in patch &&
        'fontSize' in patch &&
        !('content' in patch) &&
        !('fontFamily' in patch) &&
        !('fontWeight' in patch) &&
        !('curve' in patch) &&
        !('curveRadius' in patch);

      if (isUniformScale) {
        return {
          ...merged,
          curveGlyphs: curve === 'arc-up' || curve === 'arc-down' ? merged.curveGlyphs : undefined,
        };
      }

      const metricsChanged =
        'content' in patch ||
        'fontFamily' in patch ||
        'fontSize' in patch ||
        'fontWeight' in patch ||
        'lineHeight' in patch ||
        'curve' in patch ||
        'curveRadius' in patch;

      if (!metricsChanged) {
        return merged;
      }

      return remasureTextObject(merged, printArea(state.model, state.activeView));
    });
    set({
      document: withObjects(state.document, state.activeView, next),
      past: [
        ...state.past,
        {
          undo: () => set({ document: withObjects(get().document, state.activeView, prev) }),
          redo: () => set({ document: withObjects(get().document, state.activeView, next) }),
        },
      ],
      future: [],
    });
  },

  deleteSelected() {
    const state = get();
    if (!state.selectedId) return;
    const prev = activeObjects(state);
    const next = prev.filter((o) => o.id !== state.selectedId);
    set({
      document: withObjects(state.document, state.activeView, next),
      selectedId: null,
      panel: 'none',
      past: [
        ...state.past,
        {
          undo: () => set({ document: withObjects(get().document, state.activeView, prev) }),
          redo: () => set({ document: withObjects(get().document, state.activeView, next) }),
        },
      ],
      future: [],
    });
  },

  centerH() {
    const state = get();
    const area = printArea(state.model, state.activeView);
    const obj = activeObjects(state).find((o) => o.id === state.selectedId);
    if (!obj) return;
    get().updateObject(obj.id, { x: (area.width - obj.width) / 2 });
  },

  centerV() {
    const state = get();
    const area = printArea(state.model, state.activeView);
    const obj = activeObjects(state).find((o) => o.id === state.selectedId);
    if (!obj) return;
    get().updateObject(obj.id, { y: (area.height - obj.height) / 2 });
  },

  bringForward() {
    const state = get();
    const prev = [...activeObjects(state)];
    const i = prev.findIndex((o) => o.id === state.selectedId);
    if (i < 0 || i >= prev.length - 1) return;
    const next = [...prev];
    const tmp = next[i]!;
    next[i] = next[i + 1]!;
    next[i + 1] = tmp;
    set({ document: withObjects(state.document, state.activeView, next) });
  },

  sendBackward() {
    const state = get();
    const prev = [...activeObjects(state)];
    const i = prev.findIndex((o) => o.id === state.selectedId);
    if (i <= 0) return;
    const next = [...prev];
    const tmp = next[i]!;
    next[i] = next[i - 1]!;
    next[i - 1] = tmp;
    set({ document: withObjects(state.document, state.activeView, next) });
  },

  moveSelectedToView(viewId) {
    const state = get();
    if (!state.selectedId || viewId === state.activeView) return;
    const from = activeObjects(state);
    const obj = from.find((o) => o.id === state.selectedId);
    if (!obj) return;

    const fromArea = printArea(state.model, state.activeView);
    const toArea = printArea(state.model, viewId);
    const sx = toArea.width / Math.max(fromArea.width, 0.01);
    const sy = toArea.height / Math.max(fromArea.height, 0.01);

    // Keep relative size/position inside the print area across views.
    let moved = scaleObject(obj, sx, sy);
    if (moved.width > toArea.width || moved.height > toArea.height) {
      const fit = Math.min(
        toArea.width / Math.max(moved.width, 0.01),
        toArea.height / Math.max(moved.height, 0.01),
      );
      moved = scaleObject(moved, fit, fit);
    }
    moved = {
      ...moved,
      x: Math.max(0, Math.min(moved.x, Math.max(0, toArea.width - moved.width))),
      y: Math.max(0, Math.min(moved.y, Math.max(0, toArea.height - moved.height))),
    };

    const remaining = from.filter((o) => o.id !== obj.id);
    let doc = withObjects(state.document, state.activeView, remaining);
    const targetView = doc.views.find((v) => v.viewId === viewId);
    if (!targetView) {
      doc = {
        ...doc,
        views: [
          ...doc.views,
          {
            viewId,
            printAreaId: `${viewId}-main`,
            objects: [moved],
          },
        ],
      };
    } else {
      doc = withObjects(doc, viewId, [...targetView.objects, moved]);
    }
    set({
      document: doc,
      activeView: viewId,
      selectedId: moved.id,
      panel: obj.type === 'text' ? 'text' : 'design',
    });
  },

  undo() {
    const { past, future } = get();
    const cmd = past[past.length - 1];
    if (!cmd) return;
    cmd.undo();
    set({ past: past.slice(0, -1), future: [...future, cmd] });
  },

  redo() {
    const { past, future } = get();
    const cmd = future[future.length - 1];
    if (!cmd) return;
    cmd.redo();
    set({ future: future.slice(0, -1), past: [...past, cmd] });
  },

  replaceDocument(doc) {
    set({ document: doc, selectedId: null, past: [], future: [] });
  },

  remasureAllText() {
    const state = get();
    const views = state.document.views.map((v) => {
      const area = printArea(state.model, v.viewId);
      return {
        ...v,
        objects: v.objects.map((o) => (o.type === 'text' ? remasureTextObject(o, area) : o)),
      };
    });
    set({
      document: {
        ...state.document,
        views,
        updatedAt: new Date().toISOString(),
      },
    });
  },

  setLowResWarning(v) {
    set({ lowResWarning: v });
  },
}));
